import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  ActionDropdown,
  ActionDropdownAction,
  Button,
  DateRangeFilter,
  InputSearch,
  Pagination,
  Status,
  TableData,
  UniversalFilter,
  ResetFiltersButton,
  FilterChips,
  formatHandoverSchedule,
} from '@/components';
import {
  useListQuery,
  useNavigateFromList,
  usePageClamp,
} from '@/hooks/useUrlListState';
import { listDefaults } from '@/utils/urlListState';
import { Column } from '@/types/common';
import { RENT_TYPE_LABEL, RentType } from '@/types/package';
import {
  ARREARS_BUCKET_LABEL,
  ARREARS_BUCKET_ORDER,
  ARREARS_BUCKET_STAT_KEY,
  ArrearsBucket,
  EscalationStatus,
  ESCALATION_STATUS_LABEL,
  GetRentalStatsParams,
  LIVE_RENTAL_STATUSES,
  RentalListItem,
  RentalPaymentStatus,
  RENTAL_PAYMENT_STATUS_LABEL,
  RentalStats,
  RentalStatsWithArrears,
  RentalStatus,
  RENTAL_STATUS_LABEL,
  HandoverStatus,
} from '@/types/rental';
import {
  getRentals,
  getRentalStats,
  holdEscalation,
  markShutdownDone,
  resumeEscalation,
  retryRefundPayout,
} from '@/services/api/rentals';
import {
  showErrorToast,
  showSuccessToast,
} from '@/components/toast/globalToast';
import { getListPitstop, Pitstop } from '@/services/api/pitstops';
import { extractApiErrorMessage, formatDateToApi } from '@/utils';
import { formatRupiah } from '@/utils/format';
import {
  canRetryRefund,
  computeDurationDays,
  escalationToneClass,
  formatArrears,
  formatVehicleLabel,
  refundStatusLabel,
  refundToneClass,
  resolveRentalStatusKey,
  showsEscalationPill,
  canRequestRepossession,
  DayRangeInput,
  parseDayRange,
  repossessionLinkLabel,
} from './rentalDisplay';

/** Keys match `RentalStats`, so a tab reads its own badge by key. */
type ActivityTab = keyof RentalStats;

const TABS: Array<{ key: ActivityTab; label: string }> = [
  { key: 'active', label: 'Active' },
  { key: 'waitingHandover', label: 'Waiting Handover' },
  { key: 'waitingReturn', label: 'Waiting Return' },
  { key: 'overdue', label: 'Overdue' },
  { key: 'ended', label: 'Selesai' },
  { key: 'completed', label: 'Lunas' },
  { key: 'others', label: 'Lainnya' },
];

/**
 * What each tab pins on the list request.
 *
 * The tabs read TWO columns. Waiting Handover and Waiting Return filter
 * `handoverStatus` — the rental is ACTIVE for both, so `status` cannot tell a
 * unit at the hub from one on the road. Active sends BOTH because it is the
 * conjunction; the wide reading ("any ACTIVE rental") would make it a superset
 * of the other two and the three badges would count the same rentals repeatedly.
 *
 * The six status tabs still *partition*: every rental appears under exactly
 * one, which is what `others` exists for. So none is invisible even though the
 * screen has no Status filter any more.
 *
 * `overdue` is the odd one out: it filters the derived `isOverdue` flag rather
 * than a status, so it sits on top of that partition and deliberately overlaps
 * `active` and `waitingReturn` — the same rental shows under two tabs. That
 * overlap is why the badges are per-tab counts and are never summed into a
 * total: adding them double-counts.
 */
const TAB_QUERY: Record<
  ActivityTab,
  {
    status?: RentalStatus[];
    handoverStatus?: HandoverStatus[];
    isOverdue?: true;
    isOther?: true;
  }
> = {
  active: {
    status: [RentalStatus.ACTIVE],
    handoverStatus: [HandoverStatus.ACTIVE],
  },
  waitingHandover: { handoverStatus: [HandoverStatus.WAITING_HANDOVER] },
  waitingReturn: { handoverStatus: [HandoverStatus.WAITING_RETURN] },
  // Server-derived from the same constant that produces `arrears`, so the
  // filter and the day counts cannot disagree.
  overdue: { isOverdue: true },
  ended: { status: [RentalStatus.ENDED] },
  completed: { status: [RentalStatus.COMPLETED] },
  // A server-side NOT IN across BOTH columns rather than a status list this
  // screen writes out: the counts endpoint derives the badge the same way, so
  // the tab and its number stay in step even as either enum grows.
  others: { isOther: true },
};

const EMPTY_STATE: Record<ActivityTab, { title: string; body: string }> = {
  active: {
    title: 'Tidak Ada Sewa Berjalan',
    body: 'Sewa akan muncul di sini setelah unit diserahterimakan ke mitra.',
  },
  waitingHandover: {
    title: 'Tidak Ada Sewa Menunggu Serah Terima',
    body: 'Booking yang sudah dibayar dan menunggu penyerahan unit akan muncul di sini.',
  },
  waitingReturn: {
    title: 'Tidak Ada Sewa Menunggu Pengembalian',
    body: 'Sewa dengan pengajuan pengembalian unit akan muncul di sini.',
  },
  overdue: {
    title: 'Tidak Ada Sewa Overdue',
    body: 'Semua mitra sedang lancar membayar sewa harian.',
  },
  ended: {
    title: 'Tidak Ada Sewa Selesai',
    body: 'Sewa yang sudah selesai dan unitnya kembali akan muncul di sini.',
  },
  completed: {
    title: 'Tidak Ada Sewa Lunas',
    body: 'RTO yang tenornya sudah lunas dan unitnya menjadi milik mitra akan muncul di sini.',
  },
  others: {
    title: 'Tidak Ada Sewa Lainnya',
    body: 'Pre order, request booking, dan sewa yang dibatalkan akan muncul di sini.',
  },
};

interface RentalFilters extends Record<string, any> {
  paymentStatus?: RentalPaymentStatus[];
  escalationStatus?: EscalationStatus[];
  contractType?: RentType[];
  // Single-select in UniversalFilter, so a scalar string.
  hubID?: string;
  hasRepossession?: 'true' | 'false';
}

/** The escalation rungs ops acts on, as quick chips on the Overdue tab. */
const OVERDUE_ESCALATION_CHIPS: EscalationStatus[] = [
  EscalationStatus.WARNED,
  EscalationStatus.SHUTDOWN_PENDING,
  EscalationStatus.SHUTDOWN,
  EscalationStatus.REPO_REQUESTED,
  EscalationStatus.HOLD,
];

/**
 * The filters the list and the counts share — everything on this screen except
 * the tab itself and the page.
 *
 * Built once for both requests on purpose: the badge is a label on the table
 * beside it, so a filter that reached one and missed the other would show a
 * count for rows the table is not displaying.
 */
const buildSharedFilterParams = (
  search: string,
  filters: RentalFilters
): GetRentalStatsParams => ({
  ...(search.trim() ? { search: search.trim() } : {}),
  ...(filters.paymentStatus?.length
    ? { paymentStatus: filters.paymentStatus }
    : {}),
  ...(filters.escalationStatus?.length
    ? { escalationStatus: filters.escalationStatus }
    : {}),
  ...(filters.contractType?.length
    ? { contractType: filters.contractType[0] }
    : {}),
  ...(filters.hubID ? { hubID: Number(filters.hubID) } : {}),
  ...(filters.hasRepossession
    ? { hasRepossession: filters.hasRepossession === 'true' }
    : {}),
});

/**
 * Which scheduled column each tab is waiting on. Only the two waiting tabs have
 * one: a date range means nothing on Active (nothing is pending) and on Ended or
 * Lunas it would be asking about a date the range does not name.
 */
const TAB_SCHEDULE_FIELD: Partial<
  Record<ActivityTab, { start: string; end: string; label: string }>
> = {
  waitingHandover: {
    start: 'scheduledHandoverStartDate',
    end: 'scheduledHandoverEndDate',
    label: 'Jadwal Serah Terima',
  },
  waitingReturn: {
    start: 'scheduledReturnStartDate',
    end: 'scheduledReturnEndDate',
    label: 'Jadwal Pengembalian',
  },
};

/**
 * The tab's own date range, as query params.
 *
 * Goes to the list AND the counts endpoint: the server narrows only the badge of
 * the tab whose column is named, so the badge above a filtered table keeps
 * matching the table.
 */
interface DateRangeValue {
  from: Date | null;
  to: Date | null;
}

const buildScheduleParams = (
  tab: ActivityTab,
  range: DateRangeValue
): Partial<GetRentalStatsParams> => {
  const field = TAB_SCHEDULE_FIELD[tab];
  if (!field) return {};
  return {
    ...(range.from ? { [field.start]: formatDateToApi(range.from) } : {}),
    ...(range.to ? { [field.end]: formatDateToApi(range.to) } : {}),
  };
};

const csvCell = (value: string | number | null | undefined): string => {
  const text = value === null || value === undefined ? '' : String(value);
  return `"${text.replace(/"/g, '""')}"`;
};

const overdueDays = (rental: RentalListItem): number =>
  rental.arrears?.days ?? 0;

/** `?tab=` from the dashboard's stat cards; anything unknown falls back. */
const isActivityTab = (value: string | null): value is ActivityTab =>
  TABS.some((tab) => tab.key === value);

const RENT_FILTER_KEYS = [
  'paymentStatus',
  'escalationStatus',
  'contractType',
  'hubID',
  'hasRepossession',
] as const;

/**
 * Everything ops narrows this list by, in the URL so a refresh keeps it. `tab`
 * keeps the name the dashboard's stat cards already link with.
 */
const RENT_LIST_DEFAULTS = listDefaults({
  tab: 'active' as string,
  bucket: null as string | null,
  dmin: '',
  dmax: '',
  arrearsSort: true as boolean,
  paymentStatus: [] as string[],
  escalationStatus: [] as string[],
  contractType: [] as string[],
  hubID: '',
  hasRepossession: '',
});

const RENT_LIST_VALIDATORS = {
  tab: (v: string) => isActivityTab(v),
  bucket: (v: string | null) =>
    v === null || Object.values(ArrearsBucket).includes(v as ArrearsBucket),
  dmin: (v: string) => /^\d*$/.test(v),
  dmax: (v: string) => /^\d*$/.test(v),
  hasRepossession: (v: string) => v === '' || v === 'true' || v === 'false',
};

const RentActivityPage: React.FC = () => {
  const navigate = useNavigate();
  const openFromList = useNavigateFromList();
  // Tab, search, filters, ranges, sort and page live in the URL, so a refresh
  // (or Back from a rental) lands on the same list.
  const {
    state: listState,
    set: setListState,
    searchTerm,
    currentPage,
    pageSize,
    activeFilters: urlFilters,
    dateRange,
    datePickerSeed,
    controlsKey,
    searchInput,
    setSearchInput,
    setSearchTerm,
    setCurrentPage,
    setFilters: setActiveFilters,
    setDateRange,
    setPageSize,
    hasFilters,
    resetFilters,
    clampToTotal,
  } = useListQuery(RENT_LIST_DEFAULTS, RENT_FILTER_KEYS, RENT_LIST_VALIDATORS);
  const activeTab = listState.tab as ActivityTab;
  const activeFilters = urlFilters as RentalFilters;
  const [rows, setRows] = useState<RentalListItem[]>([]);
  const [serverTotal, setServerTotal] = useState(0);
  const [isLoading, setIsLoading] = useState(true);
  // `null` covers both "not fetched yet" and "the counts endpoint failed" —
  // in either case the tabs simply render without badges.
  const [stats, setStats] = useState<RentalStatsWithArrears | null>(null);
  const [isStatsLoading, setIsStatsLoading] = useState(true);
  const [actionDropdownOpen, setActionDropdownOpen] = useState<
    string | number | null
  >(null);

  const isOverdueTab = activeTab === 'overdue';
  // Which arrears-day bucket the Overdue tab is narrowed to; null = every
  // overdue rental. Only that tab can hold one, so switching tabs clears it.
  const arrearsBucket = listState.bucket as ArrearsBucket | null;
  // Typed vs applied: the range only refetches on Enter or blur, not per key.
  const dayRange = useMemo<DayRangeInput>(
    () => ({ min: listState.dmin, max: listState.dmax }),
    [listState.dmin, listState.dmax]
  );
  const [dayRangeInput, setDayRangeInput] = useState<DayRangeInput>(dayRange);
  // Typed-but-unapplied, like the search box: follow the URL when the list is
  // reset or the URL changes under it (a sidebar link, Back/Forward).
  useEffect(() => {
    setDayRangeInput(dayRange);
    // Only on those events; following every dayRange change would also fire on
    // the user's own Enter, where the input already holds the value.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [controlsKey]);
  const dayRangeParams = parseDayRange(dayRange);
  const dayRangeInvalid = parseDayRange(dayRangeInput) === null;
  // Worst debtor first by default on Overdue; ops chases the biggest debt.
  const sortByArrears = listState.arrearsSort;
  const [hubs, setHubs] = useState<Pitstop[]>([]);

  useEffect(() => {
    getListPitstop({ page: '1', size: '9999', type: 'HUB', hasStaff: true })
      .then((res) => setHubs(res.data || []))
      .catch(() => setHubs([]));
  }, []);
  const scheduleField = TAB_SCHEDULE_FIELD[activeTab];

  const handleToggleDropdown = useCallback((id: string | number) => {
    setActionDropdownOpen((prev) => (prev === id ? null : id));
  }, []);

  useEffect(() => {
    const handleOutsideClick = () => setActionDropdownOpen(null);
    if (actionDropdownOpen !== null) {
      document.addEventListener('click', handleOutsideClick);
    }
    return () => document.removeEventListener('click', handleOutsideClick);
  }, [actionDropdownOpen]);

  // No Status filter here: the tabs own `status` now, and a second status
  // control would fight the tab for the same query param — and the counts
  // endpoint, which takes no status at all, could not follow it.
  const filterConfigs = useMemo(
    () => [
      {
        key: 'paymentStatus',
        title: 'Pembayaran',
        icon: 'ri-wallet-3-line',
        multiple: true,
        staticOptions: Object.values(RentalPaymentStatus).map((status) => ({
          key: status.toLowerCase(),
          label: RENTAL_PAYMENT_STATUS_LABEL[status],
          value: status,
        })),
      },
      {
        key: 'escalationStatus',
        title: 'Eskalasi',
        icon: 'ri-alarm-warning-line',
        multiple: true,
        staticOptions: Object.values(EscalationStatus).map((status) => ({
          key: status.toLowerCase(),
          label: ESCALATION_STATUS_LABEL[status],
          value: status,
        })),
      },
      {
        key: 'contractType',
        title: 'Type',
        icon: 'ri-file-paper-2-line',
        multiple: true,
        staticOptions: Object.values(RentType).map((type) => ({
          key: type.toLowerCase(),
          label: RENT_TYPE_LABEL[type],
          value: type,
        })),
      },
      {
        key: 'hubID',
        title: 'Hub',
        icon: 'ri-building-line',
        multiple: false,
        dataSource: hubs,
        labelField: 'name',
        valueField: 'ID',
      },
      {
        key: 'hasRepossession',
        title: 'Case Repossession',
        icon: 'ri-car-line',
        multiple: false,
        staticOptions: [
          { key: 'repo-yes', label: 'Ada case', value: 'true' },
          { key: 'repo-no', label: 'Tidak ada case', value: 'false' },
        ],
      },
    ],
    [hubs]
  );

  // One object, because the six positional arguments this used to take had to be
  // spelled out identically at three call sites and read as noise at all three.
  const fetchRentals = useCallback(
    async (query: {
      page: number;
      search: string;
      filters: RentalFilters;
      size: number;
      tab: ActivityTab;
      range: DateRangeValue;
      bucket: ArrearsBucket | null;
      days: ReturnType<typeof parseDayRange>;
      sortArrears: boolean;
    }) => {
      const { page, search, filters, size, tab, range, bucket, days } = query;
      const overdue = tab === 'overdue';
      setIsLoading(true);
      try {
        const response = await getRentals({
          page,
          size,
          ...TAB_QUERY[tab],
          ...buildSharedFilterParams(search, filters),
          ...buildScheduleParams(tab, range),
          // Narrower than the tab's own isOverdue, and only ever set on it.
          ...(bucket ? { arrearsBucket: bucket } : {}),
          ...(overdue && days ? days : {}),
          ...(overdue && query.sortArrears ? { sort: 'arrears' as const } : {}),
        });

        setRows(response?.items || []);
        setServerTotal(response?.pagination?.total || 0);
      } catch (error) {
        showErrorToast(
          'Error',
          extractApiErrorMessage(error, 'Failed to load rent activity')
        );
        setRows([]);
        setServerTotal(0);
      } finally {
        setIsLoading(false);
      }
    },
    []
  );

  /**
   * Tab badge counts. Failure is silent on purpose: the counts are decoration
   * on a table that works without them, and the endpoint may not be deployed
   * everywhere yet — a 404 here must not toast or blank the list.
   */
  const fetchStats = useCallback(
    async (
      search: string,
      filters: RentalFilters,
      // The one tab-dependent input a badge honours: the range names one tab's
      // column, and the server narrows that tab's bucket alone. Passed already
      // built, so the effect below can depend on its CONTENT.
      schedule: Partial<GetRentalStatsParams>
    ) => {
      setIsStatsLoading(true);
      try {
        const response = await getRentalStats({
          ...buildSharedFilterParams(search, filters),
          ...schedule,
        });
        setStats(response ?? null);
      } catch {
        setStats(null);
      } finally {
        setIsStatsLoading(false);
      }
    },
    []
  );

  useEffect(() => {
    fetchRentals({
      page: currentPage,
      search: searchTerm,
      filters: activeFilters,
      size: pageSize,
      tab: activeTab,
      range: dateRange,
      bucket: arrearsBucket,
      days: dayRangeParams,
      sortArrears: sortByArrears,
    });
    // dayRangeParams is rebuilt per render; dayRange is what actually changed.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [
    fetchRentals,
    currentPage,
    searchTerm,
    activeFilters,
    pageSize,
    activeTab,
    dateRange,
    arrearsBucket,
    dayRange,
    sortByArrears,
  ]);

  // A page restored from the URL can sit past the end once the list shrinks.
  usePageClamp(clampToTotal, serverTotal, isLoading);

  // Counts follow the non-tab inputs — the search box and the filters — plus the
  // date range, which is per tab but which the server applies per bucket, so the
  // badge above a date-filtered table still counts that table's rows.
  //
  // Keyed on the range's CONTENT, not the object: switching tabs resets it to a
  // fresh `{from: null, to: null}`, whose params are identical to the ones just
  // answered, and this is a full aggregate scan — clicking through the seven tabs
  // would otherwise cost six repeat scans. Page and page size never refetch at
  // all; they cannot change what a badge counts.
  const scheduleParams = useMemo(
    () => buildScheduleParams(activeTab, dateRange),
    [activeTab, dateRange]
  );
  const scheduleKey = JSON.stringify(scheduleParams);
  useEffect(() => {
    fetchStats(searchTerm, activeFilters, scheduleParams);
    // scheduleParams is rebuilt per render; scheduleKey is what actually changed.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [fetchStats, searchTerm, activeFilters, scheduleKey]);

  const total = serverTotal;
  const visibleRows = rows;

  const applyDayRange = () => {
    if (parseDayRange(dayRangeInput) === null) return;
    if (
      dayRangeInput.min === dayRange.min &&
      dayRangeInput.max === dayRange.max
    ) {
      return;
    }
    const min = dayRangeInput.min.trim();
    const max = dayRangeInput.max.trim();
    setListState({
      dmin: min,
      dmax: max,
      // A free range and a chip are one question; the range replaces the chip.
      ...(min || max ? { bucket: null } : {}),
      page: 1,
    });
  };

  // The escalation chips write the same filter state UniversalFilter does, so
  // the two controls can never disagree; a chip shows only for a single rung.
  const escalationChip =
    activeFilters.escalationStatus?.length === 1
      ? activeFilters.escalationStatus[0]
      : null;
  const setEscalationChip = (status: EscalationStatus | null) => {
    const next: RentalFilters = { ...activeFilters };
    if (status) next.escalationStatus = [status];
    else delete next.escalationStatus;
    setActiveFilters(next);
  };

  const handleSearch = (value: string) => {
    setSearchTerm(value);
  };

  const handleTabChange = (tab: ActivityTab) => {
    setListState({
      tab,
      page: 1,
      // The bucket and day range belong to the Overdue tab; carried over they
      // would filter a list that has no arrears column to show for them.
      bucket: null,
      dmin: '',
      dmax: '',
      // A range set here means a different column on the next tab, so carrying
      // it over would silently re-ask a question the user never asked.
      from: null,
      to: null,
    });
    setDayRangeInput({ min: '', max: '' });
  };

  const runAction = async (
    label: string,
    action: () => Promise<unknown>
  ): Promise<void> => {
    try {
      await action();
      showSuccessToast('Success', label);
      fetchRentals({
        page: currentPage,
        search: searchTerm,
        filters: activeFilters,
        size: pageSize,
        tab: activeTab,
        range: dateRange,
        bucket: arrearsBucket,
        days: dayRangeParams,
        sortArrears: sortByArrears,
      });
    } catch (error) {
      showErrorToast('Error', extractApiErrorMessage(error, 'Action failed'));
    }
  };

  /**
   * Client-side export of the rows currently loaded — the contract documents
   * no rental export endpoint. Carries more than the table shows, since an
   * export is more useful with the hub and arrears figures included.
   */
  const handleExport = () => {
    if (!visibleRows.length) {
      showErrorToast('Error', 'Tidak ada data untuk diexport');
      return;
    }

    const header = [
      'Mitra Name',
      'Mitra Code',
      'Plate',
      'Vehicle',
      'Package',
      'Contract Type',
      'Status',
      'Payment',
      'Escalation',
      'Hub',
      'Duration (Hari)',
      'Overdue (Hari)',
      'Arrears (Rp)',
    ];

    const rowsCsv = visibleRows.map((rental) =>
      [
        rental.driver?.name,
        rental.driver?.code,
        rental.vehicle?.platNomor,
        formatVehicleLabel(rental.vehicle),
        rental.package?.name,
        rental.package?.contractType,
        RENTAL_STATUS_LABEL[rental.status] ?? rental.status,
        rental.dpPaid ? 'DP Lunas' : 'DP Belum Dibayar',
        ESCALATION_STATUS_LABEL[rental.escalationStatus] ??
          rental.escalationStatus,
        rental.hub?.name,
        computeDurationDays(rental.handoverDate, rental.endDate),
        overdueDays(rental),
        rental.arrears?.amount ?? 0,
      ]
        .map(csvCell)
        .join(',')
    );

    const blob = new Blob(
      [[header.map(csvCell).join(','), ...rowsCsv].join('\n')],
      { type: 'text/csv;charset=utf-8;' }
    );
    const url = window.URL.createObjectURL(blob);
    const link = document.createElement('a');
    link.href = url;
    link.download = `rent-activity-${activeTab}_${new Date()
      .toISOString()
      .slice(0, 10)}.csv`;
    document.body.appendChild(link);
    link.click();
    link.remove();
    window.URL.revokeObjectURL(url);
    showSuccessToast('Success', `${visibleRows.length} baris diexport`);
  };

  const columns: Column<RentalListItem>[] = [
    {
      key: 'driver',
      title: 'Mitra Name',
      render: (_value: unknown, record: RentalListItem) => (
        <div className="leading-tight">
          <div className="text-[14px] text-gray-900">
            {record.driver?.name || '-'}
          </div>
          <div className="text-[12px] text-gray-500">
            {[record.driver?.code, record.code].filter(Boolean).join(' • ') ||
              '-'}
          </div>
        </div>
      ),
    },
    {
      key: 'vehicle',
      title: 'Vehicle',
      render: (_value: unknown, record: RentalListItem) =>
        record.vehicle ? (
          <div className="leading-tight">
            <div className="text-[14px] text-gray-900">
              {record.vehicle.platNomor || '-'}
            </div>
            <div className="text-[12px] text-gray-500">
              {formatVehicleLabel(record.vehicle) || '-'}
            </div>
          </div>
        ) : (
          // vehicle is null until a unit is assigned (PRE_ORDER).
          <span className="text-[12px] text-gray-400">Belum ditugaskan</span>
        ),
    },
    {
      key: 'package',
      title: 'Package',
      render: (_value: unknown, record: RentalListItem) =>
        record.package?.name ? (
          // As ops typed it. Upper-casing it used to be how the chip matched a
          // per-name style; there is one style now, so the name can read
          // normally.
          <Status
            value={record.package.name}
            type="packageScheme"
            size="small"
          />
        ) : (
          <span className="text-gray-400">-</span>
        ),
    },
    {
      key: 'status',
      title: 'Status',
      render: (_value: unknown, record: RentalListItem) => (
        <Status
          value={resolveRentalStatusKey(record)}
          type="rentalStatus"
          size="medium"
        />
      ),
    },
    // The escalation rung used to be filter-only, which meant the one column
    // that predicts a shutdown was invisible on the screen ops watch.
    {
      key: 'escalationStatus',
      title: 'Eskalasi',
      // A paused rental's rung is frozen, not live: "Dijeda" says so, and the
      // frozen rung is on the detail banner (design review D14).
      render: (_value: unknown, record: RentalListItem) =>
        record.billingPaused ? (
          <Status value="billingPaused" type="escalationStatus" size="medium" />
        ) : showsEscalationPill(record.escalationStatus) ? (
          <Status
            value={record.escalationStatus}
            type="escalationStatus"
            size="medium"
          />
        ) : (
          <span className="text-gray-400">-</span>
        ),
    },
    // The date the tab is waiting on, and only on the tabs that are waiting: a
    // Waiting Handover row has no handoverDate and a Waiting Return row no
    // endDate, so the plan is the only date either can show.
    ...(activeTab === 'waitingHandover'
      ? [
          {
            key: 'slot',
            title: 'Jadwal Serah Terima',
            render: (_value: unknown, record: RentalListItem) => (
              <span className="text-[14px] text-gray-900">
                {formatHandoverSchedule(record.slot?.date, {
                  startTime: record.slot?.startTime,
                  endTime: record.slot?.endTime,
                })}
              </span>
            ),
          } as Column<RentalListItem>,
        ]
      : []),
    ...(activeTab === 'waitingReturn'
      ? [
          {
            key: 'scheduledReturnDate',
            title: 'Jadwal Pengembalian',
            render: (_value: unknown, record: RentalListItem) => (
              <span className="text-[14px] text-gray-900">
                {/* Date only — the slot window belongs to the pickup. */}
                {formatHandoverSchedule(record.scheduledReturnDate)}
              </span>
            ),
          } as Column<RentalListItem>,
        ]
      : []),
    // Overdue is the Overdue tab's reason for existing, so it only shows there.
    ...(isOverdueTab
      ? [
          {
            key: 'overdue',
            title: 'Tunggakan',
            render: (_value: unknown, record: RentalListItem) => {
              // Days and money together: the amount is what ops actually chase,
              // and it was previously only reachable through the CSV export.
              const label = formatArrears(record.arrears);
              return label ? (
                <span
                  className={`text-[14px] font-medium ${escalationToneClass(
                    record.escalationStatus
                  )}`}
                >
                  {label}
                </span>
              ) : (
                <span className="text-gray-400">-</span>
              );
            },
          } as Column<RentalListItem>,
        ]
      : []),
    // Every tab, not one: a blocked refund is invisible everywhere else in FMS,
    // and the rental it hangs off can be in any state.
    {
      key: 'refund',
      title: 'Refund',
      render: (_value: unknown, record: RentalListItem) => {
        const refund = record.refund;
        if (!refund) return <span className="text-gray-400">-</span>;
        return (
          <div className="leading-tight">
            <span
              className={`inline-block rounded px-2 py-0.5 text-[12px] font-medium ${refundToneClass(
                refund.payoutStatus
              )}`}
            >
              {refundStatusLabel(refund.payoutStatus)}
            </span>
            {refund.amount ? (
              <div className="mt-1 text-[12px] text-gray-500">
                {formatRupiah(refund.amount)}
              </div>
            ) : null}
          </div>
        );
      },
    },
    {
      key: 'actions',
      title: '',
      width: 'w-16',
      align: 'center',
      render: (_value: unknown, record: RentalListItem, index: number) => {
        const uniqueId = record.id || `temp-${index}`;
        const actions: ActionDropdownAction[] = [
          {
            key: 'detail',
            label: 'Lihat Detail',
            icon: 'ri-eye-line',
            onClick: () => {
              setActionDropdownOpen(null);
              openFromList(`/rent-activity/${record.id}`);
            },
          },
        ];

        if (record.escalationStatus === EscalationStatus.HOLD) {
          actions.push({
            key: 'resume',
            label: 'Lanjutkan Eskalasi',
            icon: 'ri-play-line',
            onClick: () => {
              setActionDropdownOpen(null);
              runAction('Eskalasi dilanjutkan', () =>
                resumeEscalation(record.id)
              );
            },
          });
        } else if (LIVE_RENTAL_STATUSES.includes(record.status)) {
          // WAITING_RETURN still bills and still escalates, so it needs the
          // same hold as a unit already out.
          actions.push({
            key: 'hold',
            label: 'Tahan Eskalasi',
            icon: 'ri-pause-line',
            onClick: () => {
              setActionDropdownOpen(null);
              runAction('Eskalasi ditahan', () => holdEscalation(record.id));
            },
          });
        }

        // NOT_INSTRUCTED or FAILED — see canRetryRefund.
        if (canRetryRefund(record.refund)) {
          const payoutID = record.refund.payoutID;
          actions.push({
            key: 'retry-refund',
            label: 'Instruksikan Ulang Refund',
            icon: 'ri-refund-2-line',
            onClick: () => {
              setActionDropdownOpen(null);
              runAction('Refund diinstruksikan ulang', () =>
                retryRefundPayout(payoutID)
              );
            },
          });
        }

        // The ladder said it's repo time (or the unit is already shut down)
        // and the unit is out with the driver — the only state the create
        // endpoint accepts. Lands on the detail with the form already open:
        // the case needs a reason ops should write while looking at the
        // rental, not from a list row.
        if (canRequestRepossession(record)) {
          actions.push({
            key: 'request-repo',
            label: 'Request Repossession',
            icon: 'ri-car-line',
            onClick: () => {
              setActionDropdownOpen(null);
              openFromList(`/rent-activity/${record.id}?action=request-repo`);
            },
          });
        }

        if (record.repossession) {
          const repossessionID = record.repossession.ID;
          actions.push({
            key: 'view-repo',
            label: `Lihat ${repossessionLinkLabel(record.repossession)}`,
            icon: 'ri-car-line',
            onClick: () => {
              setActionDropdownOpen(null);
              navigate(`/repossessions/${repossessionID}`);
            },
          });
        }

        // Non-connected units land in a manual-shutdown queue (TRD L3).
        if (record.escalationStatus === EscalationStatus.SHUTDOWN_PENDING) {
          actions.push({
            key: 'shutdown-done',
            label: 'Tandai Shutdown Selesai',
            icon: 'ri-shut-down-line',
            onClick: () => {
              setActionDropdownOpen(null);
              runAction('Shutdown ditandai selesai', () =>
                markShutdownDone(record.id)
              );
            },
          });
        }

        return (
          <ActionDropdown
            record={record}
            isOpen={actionDropdownOpen === uniqueId}
            onToggle={() => handleToggleDropdown(uniqueId)}
            actions={actions}
            ariaLabel={`Actions for rental ${record.driver?.name ?? record.id}`}
          />
        );
      },
    },
  ];

  return (
    <div className="bg-white p-6 max-w-full mx-auto">
      <div className="mb-4">
        <div className="flex items-center justify-between">
          <div>
            <h1 className="text-[24px] font-semibold text-gray-900 mb-1">
              Rent Activity
            </h1>
            <p className="text-[14px] text-gray-600">
              Monitor dan kelola semua aktivitas sewa mitra yang sedang
              berjalan.
            </p>
          </div>
          <Button variant="primary" size="small" onClick={handleExport}>
            <i className="ri-download-line text-[12px] mr-1"></i>
            Export
          </Button>
        </div>
      </div>

      <div className="border-b border-gray-200 mb-4">
        <nav className="flex gap-6" aria-label="Rent Activity sections">
          {TABS.map((tab) => {
            const isActive = activeTab === tab.key;
            return (
              <button
                key={tab.key}
                type="button"
                onClick={() => handleTabChange(tab.key)}
                aria-current={isActive ? 'page' : undefined}
                // Matches the handover page's tab styling (HandoverListPage):
                // brand-underlined and brand-coloured when selected, grey with a
                // hover hint otherwise, so the two list screens read as one
                // product rather than two.
                className={`-mb-px flex items-center space-x-2 border-b-2 px-1 py-2 text-sm font-medium transition-colors ${
                  isActive
                    ? 'border-[#5e2aac] text-[#5e2aac]'
                    : 'border-transparent text-gray-500 hover:border-gray-300 hover:text-gray-700'
                }`}
              >
                <span>{tab.label}</span>
                {/* A placeholder of the same size while the counts load, so the
                    tab row does not jump when the badges land. */}
                {isStatsLoading ? (
                  <span
                    aria-hidden="true"
                    className="h-[18px] w-7 animate-pulse rounded-full bg-gray-100"
                  />
                ) : (
                  // Per key, not per object: a backend that predates a tab
                  // answers 200 without that key, and `stats && …` would then
                  // render the literal "undefined" in the badge.
                  // Hidden at zero, like the handover page: an empty tab says
                  // more with no badge than with a grey "0".
                  //
                  // Per key, not per object: a backend that predates a tab
                  // answers 200 without that key, and `stats && …` would then
                  // render the literal "undefined" in the badge.
                  typeof stats?.[tab.key] === 'number' &&
                  stats[tab.key] > 0 && (
                    <span
                      className={`inline-flex items-center justify-center rounded-full px-2 py-1 text-xs font-bold leading-none ${
                        isActive
                          ? 'bg-[#5e2aac] text-white'
                          : 'bg-gray-100 text-gray-600'
                      }`}
                    >
                      {stats[tab.key]}
                    </span>
                  )
                )}
              </button>
            );
          })}
        </nav>
      </div>

      <div className="rounded-lg p-2 mb-4">
        <div className="flex items-center justify-between gap-4">
          {/* Only where a date range has an unambiguous meaning: the tab's own
              scheduled column. Left of the search box, matching the handover
              screen, and labelled with the column it filters so it cannot be
              read as "created" or "handed over". */}
          <div className="flex flex-wrap items-center gap-2">
            {/* Only on Overdue: the buckets are a breakdown of that tab's own
                predicate, and every count comes from the badge endpoint, so a
                chip lists exactly what it says. */}
            {isOverdueTab && (
              <>
                <FilterChips<ArrearsBucket>
                  ariaLabel="Filter umur tunggakan"
                  value={arrearsBucket}
                  onChange={(bucket) => {
                    // A chip and a free range are one question; the chip wins.
                    setListState({ bucket, dmin: '', dmax: '', page: 1 });
                    setDayRangeInput({ min: '', max: '' });
                  }}
                  options={ARREARS_BUCKET_ORDER.map((bucket) => ({
                    value: bucket,
                    label: ARREARS_BUCKET_LABEL[bucket],
                    count: stats?.[ARREARS_BUCKET_STAT_KEY[bucket]],
                  }))}
                />
                <form
                  className="flex items-center gap-1 text-[13px] text-gray-500"
                  onSubmit={(e) => {
                    e.preventDefault();
                    applyDayRange();
                  }}
                >
                  <span>atau</span>
                  {(['min', 'max'] as const).map((end) => (
                    <React.Fragment key={end}>
                      {end === 'max' && <span>s.d.</span>}
                      <input
                        type="number"
                        min={0}
                        inputMode="numeric"
                        aria-label={
                          end === 'min'
                            ? 'Tunggakan minimal (hari)'
                            : 'Tunggakan maksimal (hari)'
                        }
                        placeholder={end === 'min' ? 'dari' : 'sampai'}
                        value={dayRangeInput[end]}
                        onChange={(e) =>
                          setDayRangeInput((prev) => ({
                            ...prev,
                            [end]: e.target.value,
                          }))
                        }
                        onBlur={applyDayRange}
                        className={`w-16 rounded-md border px-2 py-1 text-[13px] text-gray-900 ${
                          dayRangeInvalid ? 'border-red-400' : 'border-gray-300'
                        }`}
                      />
                    </React.Fragment>
                  ))}
                  <span>hari</span>
                </form>
                <FilterChips<EscalationStatus>
                  ariaLabel="Filter eskalasi"
                  value={escalationChip}
                  onChange={setEscalationChip}
                  options={OVERDUE_ESCALATION_CHIPS.map((status) => ({
                    value: status,
                    label: ESCALATION_STATUS_LABEL[status],
                  }))}
                />
              </>
            )}
            {scheduleField && (
              <>
                <span className="text-[13px] text-gray-500">
                  {scheduleField.label}
                </span>
                {/* Keyed on the tab: the control owns its own displayed range,
                    so clearing our state on a tab switch would otherwise leave
                    the previous range on screen above an unfiltered table. */}
                <DateRangeFilter
                  key={`${activeTab}-${controlsKey}`}
                  {...datePickerSeed}
                  onDateRangeChange={(range) => {
                    setDateRange(range);
                  }}
                />
              </>
            )}
          </div>

          <div className="flex items-center gap-2">
            {isOverdueTab && (
              <select
                aria-label="Urutkan"
                value={sortByArrears ? 'arrears' : 'newest'}
                onChange={(e) => {
                  setListState({
                    arrearsSort: e.target.value === 'arrears',
                    page: 1,
                  });
                }}
                className="rounded-md border border-gray-300 bg-white px-2 py-1 text-[13px] text-gray-700"
              >
                <option value="arrears">Tunggakan terbesar</option>
                <option value="newest">Terbaru</option>
              </select>
            )}
            <InputSearch
              placeholder="Search..."
              value={searchInput}
              onChange={setSearchInput}
              onSearch={handleSearch}
              onClear={() => handleSearch('')}
              debounceMs={300}
              size="small"
            />

            <UniversalFilter
              filters={filterConfigs}
              currentFilters={activeFilters}
              onApply={(filters: RentalFilters) => {
                setActiveFilters(filters);
              }}
              onClear={() => {
                setActiveFilters({});
              }}
            />
            <ResetFiltersButton
              // The tab is where the user is, not a filter on it.
              visible={hasFilters(['tab'])}
              onReset={() => resetFilters(['tab'])}
            />
          </div>
        </div>
      </div>

      <div className="relative">
        {isLoading ? (
          <div className="bg-white rounded-lg border border-gray-200 flex items-center justify-center py-12">
            <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-primary"></div>
            <span className="ml-2 text-gray-600">Loading rent activity...</span>
          </div>
        ) : (
          <TableData
            data={visibleRows}
            columns={columns}
            size="small"
            className="min-w-full"
            showHeaderWhenEmpty
            onRowClick={(record) =>
              record.id && openFromList(`/rent-activity/${record.id}`)
            }
            emptyState={
              <div className="py-20">
                <div className="w-[148px] h-[148px] mx-auto mb-4">
                  <img
                    src="/images/no-results.png"
                    alt="No rent activity yet"
                    className="w-full h-full"
                  />
                </div>
                <h3
                  className="font-['Plus_Jakarta_Sans'] font-medium text-[18px] leading-[24px] text-gray-900 mb-2 text-center"
                  style={{ letterSpacing: '-1.5%' }}
                >
                  {EMPTY_STATE[activeTab].title}
                </h3>
                <p
                  className="font-['Plus_Jakarta_Sans'] font-normal text-[14px] leading-[20px] text-gray-500 text-center max-w-[400px] mx-auto"
                  style={{ letterSpacing: '-0.6%' }}
                >
                  {EMPTY_STATE[activeTab].body}
                </p>
              </div>
            }
          />
        )}
      </div>

      <Pagination
        currentPage={currentPage}
        totalItems={total}
        itemsPerPage={pageSize}
        onPageChange={setCurrentPage}
        onPageSizeChange={(newPageSize) => {
          setPageSize(newPageSize);
        }}
      />
    </div>
  );
};

export default RentActivityPage;
